/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable @typescript-eslint/no-unsafe-argument */

import {
  createInfiniteQuery,
  type CreateInfiniteQueryResult,
  type InfiniteQueryObserverResult
} from '@tanstack/svelte-query';

import { derived, get, writable, type Writable } from 'svelte/store';
import { cloneDeep } from 'lodash-es';

import type { FPost, IUser, SearchType } from 'garden-common';
import type { DynamicSearchParams } from '$src/types/homepage.interface';
import type { MyGalleriesSingleResponse } from './gallery.util';
import type { PeopleFiltersSchemaWithDynamic, PostFiltersSchemaWithDynamic, SortOption } from './feed.util';

import { api } from './index';

type ExploreResult =
  | { type: 'post'; data: FPost<string>; }
  | { type: 'user'; data: IUser<string>; }
  | { type: 'gallery'; data: MyGalleriesSingleResponse; }
  | { type: 'all'; data: IUser<string> | FPost<string>; };

export type ContentType = 'galleries' | 'challenge' | 'explore';

type FilterType = PostFiltersSchemaWithDynamic | PeopleFiltersSchemaWithDynamic;

const limit = 6;

export class DynamicContentProvider {
  #searchTerm = '';

  #modeStore: Writable<DynamicSearchParams['type']>;

  #filters: PostFiltersSchemaWithDynamic;

  #peopleFilters: PeopleFiltersSchemaWithDynamic;

  #allFilters: PeopleFiltersSchemaWithDynamic;

  #sort: SortOption;

  #peopleSort: SortOption;

  #allSort: SortOption;

  #searchType: SearchType;

  results: Writable<ExploreResult[]>;

  activeQuery: CreateInfiniteQueryResult;

  _searchQuery: CreateInfiniteQueryResult;

  _postsQuery: CreateInfiniteQueryResult;

  _usersQuery: CreateInfiniteQueryResult;

  _allQuery: CreateInfiniteQueryResult;

  constructor(
    private readonly ids: string[],
    private readonly type: ContentType
  ) {
    this.results = writable([]);
    this.#modeStore = writable('posts');

    this._searchQuery = createInfiniteQuery({
      queryKey: ['exploreSearch'],
      queryFn: async ({ pageParam }) => {
        const res = (await api.get(
          `/v2/feed/search/${this.#searchType ?? 'all'}/${this.#searchTerm}`,
          {
            params: { page: pageParam }
          }
        ));
        console.log(res);
        return res.data;
      },
      enabled: false,
      getNextPageParam: lastPage => lastPage.nextPage
    });

    this._postsQuery = createInfiniteQuery({
      queryKey: ['explorePosts'],
      queryFn: async ({ pageParam }) => {
        const filters: FilterType = { ...this.#filters };

        if (this.checkMatchable()) {
          filters.from = this.ids;
        }
        // if filters.from is an array of objects , convert it to array of strings _ids 
        if (filters && filters.from) {
          filters.from = filters?.from?.map((f) => f._id);
        }
        const res = await this.fetchPost(pageParam, filters);

        return res.data;
      },
      enabled: false,
      getNextPageParam: lastPage => lastPage.nextPage
    });

    this._usersQuery = createInfiniteQuery({
      queryKey: ['exploreUsers'],
      queryFn: async ({ pageParam }) => {
        const filters: FilterType = { ...this.#peopleFilters };

        if (this.checkMatchable()) {
          filters.from = this.ids;
        }

        const res = await this.fetchUsers(pageParam, filters);

        return res.data;
      },
      enabled: false,
      getNextPageParam: lastPage => lastPage.nextPage
    });

    this._allQuery = createInfiniteQuery({
      queryKey: ['exploreAll'],
      queryFn: async ({ pageParam }) => {
        const filters: FilterType = { ...this.#allFilters };

        if (this.checkMatchable()) {
          filters.from = this.ids;
        }

        const usersResult = await this.fetchUsers(pageParam, { from: filters.from });
        const postsResult = await this.fetchPost(pageParam, filters);
        return {
          nextPage: postsResult.data.nextPage,
          results: [...usersResult.data.results, ...postsResult.data.results]
        };
      },
      enabled: false,
      getNextPageParam: lastPage => lastPage.nextPage
    });

    this._searchQuery.subscribe((q) => this.#onNewQueryData(q));
    this._postsQuery.subscribe((q) => this.#onNewQueryData(q));
    this._usersQuery.subscribe((q) => this.#onNewQueryData(q));
    this._allQuery.subscribe((q) => this.#onNewQueryData(q));

    // Default query
    this.setMode('posts');

    this.activeQuery = derived(
      [this.#modeStore, this._postsQuery, this._usersQuery, this._searchQuery, this._allQuery],
      ([m, p, u, s, a]) => {
        if (m === 'posts') {
          return p;
        }

        if (m === 'users') {
          return u;
        }

        if (m === 'all') {
          return a;
        }

        return s;
      }
    );
  }

  get #mode() {
    return get(this.#modeStore);
  }

  checkMatchable() {
    return this.ids && this.ids.length && this.type !== 'explore';
  }

  setMode(mode: DynamicSearchParams['type']): void {
    this.#modeStore.set(mode);
  }

  #onNewQueryData(q: InfiniteQueryObserverResult) {
    if (!q.data) {
      return;
    }

    const reducePages = (query: InfiniteQueryObserverResult) => {
      const mapped: ExploreResult[] = [];

      for (let i = 0; i < query.data.pages.length; i += 1) {
        const page = query.data.pages[i] as { results: any[]; };

        for (let j = 0; j < page.results.length; j += 1) {
          mapped.push({ data: page.results[j], type: page.results[j].type });

        }
      }
      return mapped;
    };

    this.results.update(() => reducePages(q));
  }

  fetchNextPage() {
    return get(this.activeQuery).fetchNextPage();
  }

  setSearchTerm(searchTerm: string) {
    this.#searchTerm = searchTerm;
  }

  setFilters(filters: PostFiltersSchemaWithDynamic) {
    this.#filters = cloneDeep(filters);
  }

  setPeopleFilters(filters: PeopleFiltersSchemaWithDynamic) {
    this.#peopleFilters = cloneDeep(filters);
  }

  setAllFilters(filters: PeopleFiltersSchemaWithDynamic) {
    this.#allFilters = cloneDeep(filters);
  }

  setSort(sort: SortOption) {
    this.#sort = sort;
  }

  setAllSort(sort: SortOption) {
    this.#allSort = sort;
  }

  setPeopleSort(sort: SortOption) {
    this.#peopleSort = sort;
  }

  setSearchType(searchType: SearchType) {
    // Searchbar type change should reset the active query
    this.#searchType = searchType;

    get(this._searchQuery).remove();

    if (this.#mode === 'searchTerm') {
      this.resetActiveQuery();
    }
  }

  async fetchUsers(pageParam: number, filters: object = {}) {
    try {
      return await api.post('/v2/feed/explore/people',
        { filters },
        {
          params: {
            page: pageParam,
            limit,
            sort: this.#peopleSort
          }
        });

    } catch (e) {
      throw new Error("Feed: could not fetch users");
    }
  }

  async fetchPost(pageParam: number, filters: object = {}) {
    try {
      return await api.post(
        '/v2/feed/explore/posts',
        { filters },
        {
          params: { page: pageParam, limit, sort: this.#allSort }
        }
      );
    } catch (e) {
      console.log('Error fetching posts', e);
      throw new Error("Feed: could not fetch users");
    }
  }

  resetActiveQuery() {
    const q = get(this.activeQuery);
    q.remove();
    void q.refetch();

    this.results.set([]);
  }
}
